import { OrderStatusEnum } from '@/domains/order/typescript/OrderInterface';
import ManufactureTypeEnum from '@/domains/scan/typescript/enums/ManufactureTypeEnum';
import TemplateFittingEnum from '@/domains/sole/typescript/enums/TemplateFittingEnum';
import useAuthStore from '@/domains/authentication/store/auth.store';

import { soleList } from '@/domains/scan/services/configurationDatas';
import useSettingsStore from '@/domains/settings/store/settings.store';
import useSoleStore from '@/domains/sole/store/sole.store';
import { defaultSoleConfiguration } from '@/domains/sole/services/utils/defaultValues';
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { OrderInterface } from '@/domains/order/typescript/OrderInterface';
import type SoleConfigurationInterface from '@/domains/sole/typescript/interfaces/SoleConfigurationInterface';
import type ScanPresetInterface from '@/domains/scan/typescript/interfaces/ScanPresetInterface';
import type { OrderListInterface } from '@/domains/order/typescript/OrderListInterface';
import type OrderParametersFileInterface from '@/domains/order/typescript/OrderParametersFileInterface';

export default defineStore('order', () => {
    const settingsStore = useSettingsStore();
    const authStore = useAuthStore();
    const soleStore = useSoleStore();

    const orders = ref<OrderListInterface[]>();

    // Cancellation scope of the order being loaded. It lives in the store so that every
    // useOrderHelper() instance shares it: the view that leaves the order aborts the load
    // started by any other component, which is what keeps a late load from writing onto
    // the next order (#999). loadOrder renews it, so aborting never blocks the next load.
    const loadOrderAbortController = ref(new AbortController());

    // Set for the whole duration of a load, alongside the scope above, so that every
    // useOrderHelper() instance and every autosave sees the same answer to "is this order
    // complete enough to be persisted?" (#1021).
    const isOrderLoading = ref(false);

    // Raised when the API refused a write because the order changed elsewhere (another tab, a
    // transmission): this tab's state can no longer be saved, only reloaded.
    const isOrderStale = ref(false);

    const saveHash = ref();
    const lastSaveHash = ref();
    const isSaving = ref(false);

    const defaultOrder = computed<OrderInterface>(() => ({
        id: '',
        patient: {
            '@id': '',
            id: '',
            lastName: '',
            firstName: '',
        },
        consultant: `/office_users/${authStore.userProfile?.id}`,
        creationDate: new Date(),
        doctor: '',
        status: OrderStatusEnum.IN_CREATION,
        glued: false,
        lowCut: false,
        sanded: false,
        templateFitting: TemplateFittingEnum.None,
        cuttingLength: 0,
        manufacture: ManufactureTypeEnum.Milling,
        coveringMaterial: 0,
    }));

    function getDefaultOrder() {
        return defaultOrder.value;
    }
    const currentOrder = ref<OrderInterface>(defaultOrder.value);

    const soleConfiguration = ref<SoleConfigurationInterface>(defaultSoleConfiguration);

    const selectedOrders = ref<OrderInterface['id'][]>([]);

    const lastOrderParameters = ref<OrderParametersFileInterface>();

    const selectOrderById = (id: string | undefined) => {
        if (!id) {
            return;
        }
        selectedOrders.value.push(id);

        selectedOrders.value = [...new Set(selectedOrders.value)];
    };

    const isOrderSelected = (orderId: OrderInterface['id']): boolean => selectedOrders.value.includes(orderId);

    const unselectOrderById = (orderId: OrderInterface['id']) => {
        const index = selectedOrders.value.indexOf(orderId);
        if (index > -1) {
            selectedOrders.value.splice(index, 1);
        }
    };

    function getValidManufacture(preferred: ManufactureTypeEnum | null | undefined): ManufactureTypeEnum {
        const available = authStore.selectedUserOffice?.manufacturingType;
        if (available && available.length > 0) {
            if (preferred && available.includes(preferred)) {
                return preferred;
            }
            return available[0];
        }
        return preferred ?? soleStore.soleInformations.manufacture;
    }

    const initOrder = () => {
        isOrderStale.value = false;
        currentOrder.value = {
            ...defaultOrder.value,
            comment: settingsStore.manufacturePreferences.comment,
        };

        soleStore.soleInformations = {
            ...soleStore.soleInformations,
            manufacture: getValidManufacture(settingsStore.manufacturePreferences.manufacture),
            glued: settingsStore.manufacturePreferences.isGlued,
            sanded: settingsStore.manufacturePreferences.isSanded,
            templateFitting: TemplateFittingEnum.None,
        };
    };

    const loadingConfiguration = (soleConfigurationLoaded: SoleConfigurationInterface) => {
        soleConfiguration.value = soleConfigurationLoaded;
    };

    const applyPreset = (preset: ScanPresetInterface) => {
        soleConfiguration.value = {
            ...soleConfiguration.value,
            ...preset,
        };
    };

    function $reset() {
        console.info('reset order store');
        orders.value = undefined;
        currentOrder.value = defaultOrder.value;
        soleConfiguration.value = defaultSoleConfiguration;
        selectedOrders.value = [];
        // Reset the save-state tracking too: otherwise a stale hash from a
        // previously loaded order survives the reset and leaks into the next
        // SPA navigation, making `shouldSave` falsely true before any order
        // is loaded.
        saveHash.value = undefined;
        lastSaveHash.value = undefined;
        isSaving.value = false;
        // The parameters cache is the last-known-good elementInfos used when the soles are not
        // ready (#983). Keeping it across orders makes the next order persist the previous
        // order's elements without any race at all (#999).
        lastOrderParameters.value = undefined;
        // Resetting the order also ends its load: this covers the paths that reset the store
        // without unmounting the view, logout in particular. A fresh scope takes over right away,
        // otherwise the store would stay in a terminal aborted state and every default-scoped
        // call (checkDefaultScans, loadOriginalScan, applySolePresetToOrder) would become a
        // silent no-op until the next loadOrder. Safe: loads in flight hold their own handle.
        loadOrderAbortController.value.abort();
        loadOrderAbortController.value = new AbortController();
        // The load this reset just ended must not keep saving locked for the next order.
        isOrderLoading.value = false;
        isOrderStale.value = false;
    }

    return {
        orders,
        loadOrderAbortController,
        isOrderLoading,
        isOrderStale,
        selectedOrders,
        selectOrderById,
        isOrderSelected,
        unselectOrderById,
        currentOrder,
        initOrder,
        soleConfiguration,
        soleList,
        getDefaultOrder,
        loadingConfiguration,
        applyPreset,
        lastOrderParameters,

        saveHash,
        lastSaveHash,
        isSaving,

        defaultOrder,

        $reset,
    };
});
